import {createSlice} from "@reduxjs/toolkit";
import {FieldNames} from "../../models/fields";
import {DEFAULT_RESULTS_PER_PAGE} from "../../models/options";
import {setIncidentAssigneesSuccess} from "./assigneesSlice";
import {updateMultipleIncidentValues} from "./utils";

const initialState = {
  drawerIncident: null,
  hasNextPage: false,
  hasZeroResults: false,
  loadedResults: [],
  loading: false,
  pageCount: DEFAULT_RESULTS_PER_PAGE,
  pagesLoaded: 1,
  reset: false,
  searchText: "",
  searching: false,
  settingsDisabled: false,
  totalCount: 0,
  totalNewCount: 0,
  totalOpenCount: 0,
  totalPages: 0,
  totalRows: 0,
  totalUnassignedCount: 0
};

export const incidentsSlice = createSlice({
  name: "incidents",
  initialState,
  reducers: {
    searchIncidents: (state, action) => {
      state.searching = true;
      state.loading = true;
      state.pageCount = action.payload.pageCount;
    },
    searchIncidentsSuccess: (state, action) => {
      state.totalRows = action.payload.incidents.totalRows;
      state.searchText = action.payload.searchText;
      state.searching = false;
      state.settingsDisabled = action.payload.settingsDisabled;
      state.loading = false;
      state.loadedResults = action.payload.incidents.dataRows;
      state.reset = false;
      state.hasZeroResults = action.payload.incidents.dataRows.length === 0;
    },
    searchIncidentsFailure: (state) => {
      state.searching = false;
      state.loading = false;
      state.hasZeroResults = true;
    },
    getIncidentsCount: () => {},
    getIncidentsCountSuccess: (state, action) => {
      state.totalCount = action.payload;
    },
    getIncidentsCountFailure: () => {},
    getNewIncidentsCount: () => {},
    getNewIncidentsCountSuccess: (state, action) => {
      state.totalNewCount = action.payload;
    },
    getNewIncidentsCountFailure: () => {},
    getOpenIncidentsCount: () => {},
    getOpenIncidentsCountSuccess: (state, action) => {
      state.totalOpenCount = action.payload;
    },
    getOpenIncidentsCountFailure: () => {},
    getUnassignedIncidentsCount: () => {},
    getUnassignedIncidentsCountSuccess: (state, action) => {
      state.totalUnassignedCount = action.payload;
    },
    getUnassignedIncidentsCountFailure: () => {},
    setIncidentStatus: (state) => {
      state.loading = true;
    },
    setIncidentStatusSuccess: (state, action) => {
      state.loadedResults = updateMultipleIncidentValues(
        state.loadedResults,
        action.payload,
        FieldNames.status
      );

      const matchingIncident = action.payload?.incidents.find(
        ({id}) => id === state.drawerIncident?.id
      );

      if (matchingIncident) {
        state.drawerIncident.status = matchingIncident.status;
      }

      state.loading = false;
    },
    setIncidentStatusFailure: (state) => {
      state.loading = false;
    },
    setLinkingIncidents: (state) => {
      state.loading = true;
    },
    setLinkingIncidentsSuccess: (state) => {
      state.loading = false;
    },
    setLinkingIncidentFailure: (state) => {
      state.loading = false;
    },
    bulkDeleteIncidents: (state) => {
      state.loading = true;
    },
    bulkDeleteIncidentsSuccess: (state, action) => {
      state.loading = false;
      state.loadedResults = state.loadedResults.filter(
        (incident) => !action.payload.includes(incident.id)
      );

      if (action.payload.includes(state.drawerIncident?.id)) {
        state.drawerIncident = null;
      }
    },
    bulkDeleteIncidentsFailure: (state) => {
      state.loading = false;
    },
    setDrawerIncident: (state, action) => {
      state.drawerIncident = action.payload;
    },
    resetIncidentList: (state) => {
      state.reset = true;
    },
    setLoadingIncidents: (state, action) => {
      state.loading = action.payload;
    },
    clearIncidents: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(setIncidentAssigneesSuccess, (state, action) => {
        const {id, assignees} = action.payload;

        state.loadedResults = state.loadedResults.map((incident) =>
          incident?.id === id ? {...incident, assignees} : incident
        );

        if (state.drawerIncident?.id === id) {
          state.drawerIncident = {...state.drawerIncident, assignees};
        }
      })
      .addDefaultCase(() => {});
  }
});

export const {
  searchIncidents,
  searchIncidentsSuccess,
  searchIncidentsFailure,
  setDrawerIncident,
  getIncidentsCount,
  getIncidentsCountSuccess,
  getIncidentsCountFailure,
  getNewIncidentsCount,
  getNewIncidentsCountSuccess,
  getNewIncidentsCountFailure,
  getOpenIncidentsCount,
  getOpenIncidentsCountSuccess,
  getOpenIncidentsCountFailure,
  getUnassignedIncidentsCount,
  getUnassignedIncidentsCountSuccess,
  getUnassignedIncidentsCountFailure,
  setIncidentStatus,
  setIncidentStatusSuccess,
  setIncidentStatusFailure,
  setLinkingIncidents,
  setLinkingIncidentsSuccess,
  setLinkingIncidentFailure,
  bulkDeleteIncidents,
  bulkDeleteIncidentsSuccess,
  bulkDeleteIncidentsFailure,
  resetIncidentList,
  setLoadingIncidents,
  clearIncidents
} = incidentsSlice.actions;

export default incidentsSlice.reducer;
